import React from 'react';
import { useTranslation } from 'react-i18next';
import UserHeader from './UserHeader';
import SocialIcon from './SocialIcon';
import { getProfileStyle } from '../utils/profileStyle';
import safeHref from '../utils/safeHref';
import getEmbed from '../utils/linkEmbed';
import ProfileBlock from './ProfileBlocks';
import '../design.css';

// Which links the profile shows right now. Kept in step with visibleLinks in
// src/profileSeo.js, which renders the same page for crawlers.
export const isLinkVisible = (link, now = Date.now()) =>
  link.isActive !== false &&
  (!link.scheduledStart || new Date(link.scheduledStart).getTime() <= now) &&
  (!link.scheduledEnd || new Date(link.scheduledEnd).getTime() >= now);

// Opening hours, a location or a menu rather than a link (see ProfileBlocks)
const isBlock = link => link.kind && link.kind !== 'link';
// A 'custom' link has no glyph to stand on its own, so it stays a button
// even if it was somehow saved as an icon.
const isIcon = link => !isBlock(link) && link.display === 'icon' && link.type && link.type !== 'custom';

// Avatars are stored as "uploads/x.png" (relative) or a full Google URL. The
// relative form only resolves from a top-level path like /deniz, so make it
// absolute for pages nested deeper, such as the dashboard preview.
export const avatarSrc = (avatar) =>
  !avatar ? '/uploads/default.png' : /^https?:\/\//i.test(avatar) ? avatar : '/' + avatar.replace(/^\/+/, '');

/*
  The profile page itself: header, icon row, buttons and players. Rendered by
  the public page (UserLinkTree) and by the dashboard's live preview, so what
  a user edits is exactly what visitors get. src/profileView.js renders the
  same markup on the server for the first paint, so a change to the elements
  or class names here belongs there too.

  `preview` keeps links from navigating away and skips click tracking.
  `children` go at the bottom of the page (the public page's footer).
*/
const ProfileView = ({ user, className: extraClass = '', preview = false, onLinkClick, children }) => {
  const [t] = useTranslation();
  const links = [...(user.links || [])].sort((a, b) => a.order - b.order);
  const visibleLinks = links.filter(link => isLinkVisible(link));
  const iconLinks = visibleLinks.filter(isIcon);
  const listLinks = visibleLinks.filter(link => !isIcon(link));
  const { style, className } = getProfileStyle(user.design);

  const anchorProps = (link) => preview
    ? { href: safeHref(link.url), onClick: e => e.preventDefault() }
    // nofollow: user-submitted links must not pass this site's link equity
    : { href: safeHref(link.url), target: '_blank', rel: 'noopener noreferrer nofollow', onClick: () => onLinkClick && onLinkClick(link._id) };
  // One <main> per page; the preview already sits inside the dashboard's
  const List = preview ? 'div' : 'main';

  return (
    <div className={`${className} ${extraClass}`} style={style}>
      <UserHeader username={user.username} avatar={avatarSrc(user.avatar)} bio={user.bio} name={user.name} heading={preview ? 'h2' : 'h1'} />
      {iconLinks.length > 0 &&
        <nav className="social-row" aria-label={t('socialLinks')}>
          {iconLinks.map(link =>
            <a key={link._id} {...anchorProps(link)} aria-label={link.linkTitle} title={link.linkTitle}>
              <SocialIcon type={link.type} />
            </a>
          )}
        </nav>
      }
      <List className="linksList">
        {(listLinks.length > 0) ? (listLinks.map(link => {
          if (isBlock(link)) {
            return <ProfileBlock key={link._id} link={link} anchorProps={url => anchorProps({ _id: link._id, url })} />;
          }
          const embed = link.display === 'embed' ? getEmbed(link.url) : null;
          return embed ? (
            <div key={link._id} className={`link-embed link-embed--${embed.provider}-${embed.kind}`}>
              <iframe src={embed.src} title={link.linkTitle} loading="lazy" frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" allowFullScreen />
            </div>
          ) : (
            <div key={link._id} className={link.featured ? 'link link--featured' : 'link'}>
              <a {...anchorProps(link)} className='linkContainer'>
                <SocialIcon type={link.type} /><span className="linkTitle">{link.linkTitle}</span>
              </a>
            </div>
          );
        })
        ) : (iconLinks.length === 0 && <p className="empty-linktree">{t('userLinkIsEmpty')}</p>)}
      </List>
      {children}
    </div>
  );
}

export default ProfileView
